/**
 * CustomerSubscriptionsTab - Sub-aba de Recorrência no modal de cliente
 * Lista assinaturas/recorrências vinculadas ao customer_id, reutilizando
 * o mesmo layout/colunas/componentes da página `/financial/subscriptions`.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-19 v1.1.0 - Repassa canEdit/canDelete para SubscriptionsTable
 * RELEASE: 2026-06-10 v1.0.0 - Criação (sub-aba Recorrência da aba Financeiro)
 */
import { useState } from 'react';
import { useFinanceiroAssinaturasPaginated, PaginatedAssinatura } from '@/hooks/useFinanceiroAssinaturasPaginated';
import { SubscriptionsTable } from '@/components/financial/SubscriptionsTable';
import { SubscriptionDetailsModal } from '@/components/financial/SubscriptionDetailsModal';
import { PaginationFooter } from '@/components/ui/pagination-footer';

interface CustomerSubscriptionsTabProps {
  /** ID do cliente */
  customerId: string;
  /** Lazy loading: só busca quando a aba estiver ativa */
  isActive?: boolean;
  /** Permissão para pausar/reativar recorrência (financial_charges.can_edit) */
  canEdit?: boolean;
  /** Permissão para cancelar recorrência (financial_charges.can_delete) */
  canDelete?: boolean;
}

/**
 * Aba de recorrências dentro do modal premium de cliente.
 * Usa RPC get_assinaturas_paginated filtrando por customer_id.
 */
export function CustomerSubscriptionsTab({
  customerId,
  isActive = false,
  canEdit,
  canDelete,
}: CustomerSubscriptionsTabProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { assinaturas, totalCount, isLoading } = useFinanceiroAssinaturasPaginated(
    isActive ? { customer_id: customerId } : undefined,
    { page: currentPage, pageSize },
  );

  const handleRowClick = (a: PaginatedAssinatura) => {
    setSelectedId(a.id);
    setDetailsOpen(true);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tabela rolável */}
      <div className="flex-1 overflow-auto">
        <SubscriptionsTable
          assinaturas={assinaturas}
          isLoading={isLoading}
          onRowClick={handleRowClick}
          hideCustomer
          canEdit={canEdit}
          canDelete={canDelete}
          emptyMessage="Nenhuma recorrência encontrada para este cliente"
        />
      </div>

      {/* Paginação */}
      {!isLoading && totalCount > 0 && (
        <div className="border-t px-4 py-2 bg-background">
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={size => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            borderClassName=""
            className="mt-0 pt-0"
          />
        </div>
      )}

      {/* Modal de detalhes da recorrência */}
      <SubscriptionDetailsModal
        assinaturaId={selectedId}
        open={detailsOpen}
        onOpenChange={v => {
          setDetailsOpen(v);
          if (!v) setSelectedId(null);
        }}
      />
    </div>
  );
}
